import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, ChevronDown, Search, User, LogOut } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCustomer } from '../context/CustomerContext';
import { CATEGORIES } from '../data/products';

const NAV_LINKS = [
  { label: 'About', href: '/about' },
  { label: 'Blog', href: '#' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const { cartCount, setCartOpen } = useCart();
  const { customer, logout } = useCustomer();
  const navigate = useNavigate();

  // Close-delay refs — prevent the gap between trigger and dropdown from closing the menu
  const productsTimer = useRef(null);
  const resourcesTimer = useRef(null);

  const openProducts  = () => { clearTimeout(productsTimer.current);  setProductsOpen(true);  };
  const closeProducts = () => { productsTimer.current  = setTimeout(() => setProductsOpen(false),  120); };
  const openResources  = () => { clearTimeout(resourcesTimer.current); setResourcesOpen(true);  };
  const closeResources = () => { resourcesTimer.current = setTimeout(() => setResourcesOpen(false), 120); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/95 backdrop-blur-xl shadow-2xl shadow-black/50' : 'bg-black/70 backdrop-blur-md'
      } border-b border-white/10`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none group">
            <span className="font-bebas text-2xl lg:text-3xl text-white tracking-widest group-hover:text-orange-500 transition-colors">
              WITT MACHINE
            </span>
            <span className="font-inter text-[9px] text-zinc-500 tracking-[0.25em] uppercase -mt-0.5">
              &amp; TOOL CO. — TEXAS
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {/* Products dropdown */}
            <div
              className="relative"
              onMouseEnter={openProducts}
              onMouseLeave={closeProducts}
            >
              <button
                data-testid="nav-products-dropdown"
                className="flex items-center gap-1 font-inter text-sm font-medium text-zinc-300 hover:text-white transition-colors uppercase tracking-widest"
              >
                Products <ChevronDown size={14} className={`transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
              </button>
              {productsOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 w-[640px] bg-[#111111] border border-white/10 shadow-2xl"
                  style={{ paddingTop: '8px', marginTop: '-4px' }}
                  onMouseEnter={openProducts}
                  onMouseLeave={closeProducts}
                >
                  <div className="p-6 grid grid-cols-2 gap-3">
                    {CATEGORIES.map(cat => (
                      <Link
                        key={cat.id}
                        to={`/${cat.slug}`}
                        data-testid={`nav-cat-${cat.id}`}
                        className="flex gap-4 items-center p-3 hover:bg-white/5 border border-white/5 hover:border-orange-500/30 transition-all group"
                        onClick={() => setProductsOpen(false)}
                      >
                        <div className="w-16 h-16 bg-[#1e1e1e] overflow-hidden flex-shrink-0">
                          <img src={cat.image} alt={cat.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" onError={e => { e.target.style.display = 'none'; }} />
                        </div>
                        <div>
                          <div className="font-bebas text-base text-white group-hover:text-orange-500 transition-colors tracking-wide">{cat.name}</div>
                          <div className="font-inter text-xs text-zinc-500 leading-snug mt-0.5">{cat.tagline}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="border-t border-white/10 p-4 flex items-center justify-between">
                    <span className="font-inter text-xs text-zinc-500 uppercase tracking-widest">Browse all products</span>
                    <Link to="/shop" className="font-bebas text-sm text-orange-500 hover:text-orange-400 tracking-wider" onClick={() => setProductsOpen(false)}>
                      SHOP ALL →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Resources dropdown */}
            <div
              className="relative"
              onMouseEnter={openResources}
              onMouseLeave={closeResources}
            >
              <button
                data-testid="nav-resources-dropdown"
                className="flex items-center gap-1 font-inter text-sm font-medium text-zinc-300 hover:text-white transition-colors uppercase tracking-widest"
              >
                Resources <ChevronDown size={14} className={`transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {resourcesOpen && (
                <div
                  className="absolute top-full left-0 w-64 bg-[#111111] border border-white/10 shadow-2xl"
                  style={{ paddingTop: '8px', marginTop: '-4px' }}
                  onMouseEnter={openResources}
                  onMouseLeave={closeResources}
                >
                  {[
                    { label: 'Resources Hub', href: '/resources' },
                    { label: 'Muzzle Brake FAQ', href: '/resources/faq' },
                    { label: 'Measuring Guide', href: '/resources/measuring' },
                    { label: 'Installation Instructions', href: '/resources/installation' },
                  ].map(item => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block px-4 py-3 font-inter text-sm text-zinc-300 hover:text-white hover:bg-white/5 border-b border-white/5 last:border-0 transition-colors uppercase tracking-wider"
                      onClick={() => setResourcesOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {NAV_LINKS.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className="font-inter text-sm font-medium text-zinc-300 hover:text-white transition-colors uppercase tracking-widest"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 lg:gap-3">
            <Link
              to="/dealer-signup"
              data-testid="nav-dealer-signup"
              className="hidden lg:block font-inter text-xs font-semibold text-white border border-orange-500 px-4 py-2 hover:bg-orange-500 transition-colors uppercase tracking-widest"
            >
              Dealer Program
            </Link>
            {/* Customer account */}
            {customer ? (
              <div className="hidden lg:flex items-center gap-1">
                <Link to="/account" data-testid="nav-my-account"
                  className="flex items-center gap-1.5 font-inter text-xs text-zinc-300 hover:text-white border border-white/10 hover:border-orange-500/40 px-3 py-2 transition-colors uppercase tracking-wider">
                  <User size={13} />
                  {customer.first_name || 'Account'}
                </Link>
                <button onClick={() => { logout(); navigate('/'); }}
                  className="p-2 text-zinc-500 hover:text-white transition-colors" title="Sign out">
                  <LogOut size={15} />
                </button>
              </div>
            ) : (
              <Link to="/account/login" data-testid="nav-sign-in"
                className="hidden lg:flex items-center gap-1.5 font-inter text-xs text-zinc-300 hover:text-white border border-white/10 hover:border-orange-500/40 px-3 py-2 transition-colors uppercase tracking-wider">
                <User size={13} />Sign In
              </Link>
            )}
            <button
              data-testid="nav-cart-button"
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-zinc-300 hover:text-white transition-colors"
              aria-label="Open cart"
            >
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-zinc-300 hover:text-white"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#111111] border-t border-white/10">
          <div className="px-4 py-6 space-y-1">
            <div className="font-inter text-xs text-zinc-500 uppercase tracking-widest mb-3">Products</div>
            {CATEGORIES.map(cat => (
              <Link
                key={cat.id}
                to={`/${cat.slug}`}
                className="block py-2 font-inter text-sm text-zinc-300 hover:text-orange-500 transition-colors uppercase tracking-wider"
                onClick={() => setMobileOpen(false)}
              >
                {cat.name}
              </Link>
            ))}
            <Link to="/shop" className="block py-2 font-inter text-sm text-zinc-300 hover:text-orange-500 transition-colors uppercase tracking-wider" onClick={() => setMobileOpen(false)}>All Products</Link>
            <div className="border-t border-white/10 mt-3 pt-3 space-y-1">
              <div className="font-inter text-xs text-zinc-500 uppercase tracking-widest mb-3">Resources</div>
              <Link to="/resources/faq" className="block py-2 font-inter text-sm text-zinc-300 hover:text-orange-500 transition-colors" onClick={() => setMobileOpen(false)}>FAQ</Link>
              <Link to="/resources/measuring" className="block py-2 font-inter text-sm text-zinc-300 hover:text-orange-500 transition-colors" onClick={() => setMobileOpen(false)}>Measuring Guide</Link>
              <Link to="/resources/installation" className="block py-2 font-inter text-sm text-zinc-300 hover:text-orange-500 transition-colors" onClick={() => setMobileOpen(false)}>Installation</Link>
            </div>
            <div className="border-t border-white/10 mt-3 pt-3 space-y-1">
              <Link to="/about" className="block py-2 font-inter text-sm text-zinc-300 hover:text-orange-500 transition-colors" onClick={() => setMobileOpen(false)}>About</Link>
              <Link to="/contact" className="block py-2 font-inter text-sm text-zinc-300 hover:text-orange-500 transition-colors" onClick={() => setMobileOpen(false)}>Contact</Link>
              <Link to="/dealer-signup" className="block py-2 font-inter text-sm text-orange-500 font-semibold uppercase tracking-wider" onClick={() => setMobileOpen(false)}>Dealer Program</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
